import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { formatBRDateTime } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { PillButton } from '../../components/clients/PillButton';
import { PageHelp } from '../../components/common/PageHelp';
import { Select } from '../../components/clients/form-primitives';
import {
  useDiscardFiscalInvoice,
  useFiscalInvoices,
  useReprocessFiscalInvoice,
} from '../../hooks/use-fiscal';
import { apiClient } from '../../lib/api-client';
import { extractErrorMessage } from '../../lib/errors';
import type { FiscalInvoice } from '../../lib/fiscal-api';

type Status = 'failed_dlq' | 'processed' | 'pending' | 'discarded';
type Tab = 'webhooks' | 'fiscal';

interface WebhookEventItem {
  id: string;
  source: string;
  externalEventId: string;
  eventType: string;
  status: string;
  attemptsCount: number;
  lastError: string | null;
  receivedAt: string;
}

interface ListResponse {
  data: WebhookEventItem[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
}

/**
 * Story 8.8 — DLQ UI pra webhook_events failed_dlq.
 * Story 10.8 — aba "Fiscal" pra NFs em DLQ (failed/pending_data).
 */
export function WebhookEventsPage(): JSX.Element {
  const [tab, setTab] = useState<Tab>('webhooks');

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-5 px-6 py-6">
      <header className="flex items-start gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Eventos em DLQ</h1>
          <p className="mt-1 text-sm text-muted">
            Webhooks externos que falharam ou NFs que precisam de atenção.
          </p>
        </div>
        <PageHelp id="webhook-events" />
      </header>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-line">
        <TabButton active={tab === 'webhooks'} onClick={() => setTab('webhooks')}>
          Webhooks externos
        </TabButton>
        <TabButton active={tab === 'fiscal'} onClick={() => setTab('fiscal')}>
          NFs em DLQ
        </TabButton>
      </div>

      {tab === 'webhooks' ? <WebhooksTab /> : <FiscalDlqTab />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        '-mb-px border-b-2 px-4 py-2 text-[13px] font-medium transition-colors',
        active
          ? 'border-ink text-ink'
          : 'border-transparent text-muted hover:border-line hover:text-ink-2',
      )}
    >
      {children}
    </button>
  );
}

// ============================================================================
// Tab 1: Webhooks externos (Story 8.8 original)
// ============================================================================

function WebhooksTab(): JSX.Element {
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status>('failed_dlq');
  const [source, setSource] = useState('');

  const query = useQuery({
    queryKey: ['webhook-events', status, source],
    queryFn: async (): Promise<ListResponse> => {
      const qs = new URLSearchParams({ status });
      if (source) qs.set('source', source);
      const res = await apiClient.get<ListResponse>(`/api/webhook-events?${qs.toString()}`);
      return res.data;
    },
  });

  const retryMut = useMutation({
    mutationFn: async (id: string) => apiClient.post(`/api/webhook-events/${id}/retry`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhook-events'] }),
  });

  const discardMut = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      apiClient.post(`/api/webhook-events/${id}/discard`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhook-events'] }),
  });

  async function handleRetry(id: string): Promise<void> {
    try {
      await retryMut.mutateAsync(id);
      toast.success('Evento marcado pendente — será re-processado.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao retentar.'));
    }
  }

  async function handleDiscard(id: string): Promise<void> {
    const reason = window.prompt('Motivo do descarte:');
    if (!reason) return;
    try {
      await discardMut.mutateAsync({ id, reason });
      toast.success('Evento descartado.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao descartar.'));
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Select value={status} onChange={(e) => setStatus(e.target.value as Status)}>
          <option value="failed_dlq">Failed DLQ</option>
          <option value="pending">Pending</option>
          <option value="processed">Processed</option>
          <option value="discarded">Discarded</option>
        </Select>
        <Select value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="">Todos os sources</option>
          <option value="asaas-owl-main">asaas-owl-main</option>
          <option value="auth-owlflow">auth-owlflow</option>
          <option value="resend">resend</option>
          <option value="evolution">evolution</option>
        </Select>
      </div>

      {query.isLoading && <div className="text-sm text-muted">Carregando…</div>}

      {query.data && query.data.data.length === 0 && (
        <div className="rounded-[12px] border border-good/40 bg-good/10 py-12 text-center text-sm text-ink">
          ✅ Nada com este filtro.
        </div>
      )}

      {query.data && query.data.data.length > 0 && (
        <div className="overflow-hidden rounded-[12px] border border-line bg-panel">
          <table className="w-full text-sm">
            <thead className="border-b border-line bg-panel-2 text-left text-[11px] uppercase tracking-wider text-muted-3">
              <tr>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Event</th>
                <th className="px-4 py-3 text-right">Attempts</th>
                <th className="px-4 py-3">Recebido</th>
                <th className="px-4 py-3">Último erro</th>
                <th className="px-4 py-3 text-right">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {query.data.data.map((ev) => (
                <tr key={ev.id} className="text-ink hover:bg-panel-2">
                  <td className="px-4 py-3 font-mono text-[12px]">{ev.source}</td>
                  <td className="px-4 py-3 font-mono text-[12px]">{ev.eventType}</td>
                  <td className="px-4 py-3 text-right font-mono tnum text-muted">
                    {ev.attemptsCount}
                  </td>
                  <td className="px-4 py-3 text-muted-2 tnum">{formatBRDateTime(ev.receivedAt)}</td>
                  <td
                    className="max-w-[280px] truncate px-4 py-3 text-[11px] text-bad"
                    title={ev.lastError ?? ''}
                  >
                    {ev.lastError ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {status === 'failed_dlq' && (
                        <>
                          <PillButton type="button" onClick={() => void handleRetry(ev.id)}>
                            Retentar
                          </PillButton>
                          <PillButton
                            type="button"
                            danger
                            onClick={() => void handleDiscard(ev.id)}
                          >
                            Descartar
                          </PillButton>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Tab 2: NFs em DLQ (Story 10.8)
// ============================================================================

function FiscalDlqTab(): JSX.Element {
  const list = useFiscalInvoices({ status: ['failed', 'pending_data'], pageSize: 50 });
  const reprocess = useReprocessFiscalInvoice();
  const [discardTarget, setDiscardTarget] = useState<FiscalInvoice | null>(null);

  async function handleReprocess(inv: FiscalInvoice): Promise<void> {
    const confirmed = window.confirm(
      `Reprocessar NF da cobrança ${inv.chargeId.slice(0, 8)}? Será enfileirada pra nova tentativa.`,
    );
    if (!confirmed) return;
    try {
      await reprocess.mutateAsync(inv.id);
      toast.success('NF reprocessada — voltou pra fila.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao reprocessar.'));
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-[10px] border border-warn/30 bg-warn/5 p-3 text-[12px] text-warn">
        <strong>NFs que falharam</strong> (provider rejeitou) ou estão{' '}
        <strong>aguardando dados</strong> (CPF/CNPJ inválido, endereço incompleto). Corrija o
        cliente ou reprocesse pra retentar.
      </div>

      {list.isLoading && <div className="text-sm text-muted">Carregando…</div>}

      {list.data && list.data.data.length === 0 && (
        <div className="rounded-[12px] border border-good/40 bg-good/10 py-12 text-center text-sm text-ink">
          ✅ Nenhuma NF em DLQ.
        </div>
      )}

      {list.data && list.data.data.length > 0 && (
        <div className="overflow-hidden rounded-[12px] border border-line bg-panel">
          <table className="w-full text-sm">
            <thead className="border-b border-line bg-panel-2 text-left text-[11px] uppercase tracking-wider text-muted-3">
              <tr>
                <th className="px-4 py-3">Cobrança</th>
                <th className="px-4 py-3 text-right">Valor</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Tentativas</th>
                <th className="px-4 py-3">Erro</th>
                <th className="px-4 py-3">Última tentativa</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {list.data.data.map((inv) => (
                <tr key={inv.id} className="text-ink hover:bg-panel-2">
                  <td className="px-4 py-3 font-mono text-[11px] text-muted">
                    {inv.chargeId.slice(0, 8)}
                  </td>
                  <td className="px-4 py-3 text-right tnum">R$ {inv.amount}</td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'inline-flex items-center rounded-[6px] border px-2 py-[2px] text-[10px] font-medium uppercase tracking-wider',
                        inv.status === 'failed'
                          ? 'border-bad/40 bg-bad/10 text-bad'
                          : 'border-warn/40 bg-warn/10 text-warn',
                      )}
                    >
                      {inv.status === 'failed' ? 'Falhou' : 'Dados pendentes'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono tnum text-muted">
                    {inv.retryCount}
                  </td>
                  <td
                    className="max-w-[260px] truncate px-4 py-3 text-[11px] text-bad"
                    title={inv.errorMessage ?? ''}
                  >
                    {inv.lastErrorCode && (
                      <code className="mr-1 rounded-[4px] bg-bad/10 px-1 py-[1px] text-[10px]">
                        {inv.lastErrorCode}
                      </code>
                    )}
                    {inv.errorMessage ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-muted-2 tnum">{formatBRDateTime(inv.updatedAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <PillButton
                        type="button"
                        onClick={() => void handleReprocess(inv)}
                        disabled={reprocess.isPending}
                        icon={<RefreshCw size={12} strokeWidth={1.8} />}
                      >
                        Reprocessar
                      </PillButton>
                      <PillButton
                        type="button"
                        danger
                        onClick={() => setDiscardTarget(inv)}
                        icon={<Trash2 size={12} strokeWidth={1.8} />}
                      >
                        Descartar
                      </PillButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {discardTarget && (
        <DiscardFiscalModal
          open={Boolean(discardTarget)}
          onOpenChange={(o) => !o && setDiscardTarget(null)}
          invoice={discardTarget}
        />
      )}
    </div>
  );
}

function DiscardFiscalModal({
  open,
  onOpenChange,
  invoice,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: FiscalInvoice;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const discard = useDiscardFiscalInvoice();
  const trimmed = reason.trim();
  const valid = trimmed.length >= 10 && trimmed.length <= 500;

  async function handleConfirm(): Promise<void> {
    if (!valid) return;
    try {
      await discard.mutateAsync({ id: invoice.id, reason: trimmed });
      toast.success('NF descartada.', {
        description: 'Estado final — operador não vai mais emitir essa NF.',
      });
      onOpenChange(false);
    } catch (err) {
      toast.error('Falha ao descartar.', {
        description: extractErrorMessage(err, 'Tente de novo.'),
      });
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(520px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[14px] border border-line bg-panel p-6 shadow-lg focus:outline-none',
          )}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <Dialog.Title className="flex items-center gap-2 text-[16px] font-semibold text-ink">
              <AlertTriangle size={16} strokeWidth={2} className="text-bad" />
              Descartar NF?
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-7 w-7 items-center justify-center rounded-[6px] text-muted hover:bg-panel-2 hover:text-ink"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <Dialog.Description className="text-[13px] leading-relaxed text-muted">
            Esta NF (cobrança <code className="font-mono">{invoice.chargeId.slice(0, 8)}</code>, R${' '}
            {invoice.amount}) ficará em estado <strong className="text-ink">discarded</strong> —
            operador opta por não emitir mais.
          </Dialog.Description>

          <label className="mt-4 flex flex-col gap-2">
            <span className="text-[11px] font-medium uppercase tracking-wider text-muted-3">
              Motivo <span className="text-bad">*</span>
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={4}
              placeholder="Ex: Cliente solicitou não emitir / inelegível / erro de fluxo"
              className={cn(
                'w-full rounded-[10px] border bg-panel px-3 py-2 text-[13px] text-ink',
                'placeholder:text-muted-3 outline-none transition-colors',
                'focus:border-line-2 resize-y',
                trimmed.length > 0 && !valid ? 'border-bad/70' : 'border-line',
              )}
            />
            <div className="flex items-center justify-between text-[11px]">
              <span
                className={cn(
                  'text-muted-2',
                  trimmed.length > 0 && trimmed.length < 10 && 'text-bad',
                )}
              >
                {trimmed.length < 10 ? `Mínimo 10 caracteres (${trimmed.length}/10)` : 'Mínimo OK'}
              </span>
              <span className="text-muted-3 tnum">{trimmed.length}/500</span>
            </div>
          </label>

          <div className="mt-5 flex items-center justify-end gap-2">
            <PillButton
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={discard.isPending}
            >
              Voltar
            </PillButton>
            <PillButton
              type="button"
              danger
              disabled={!valid || discard.isPending}
              onClick={() => void handleConfirm()}
            >
              {discard.isPending ? 'Descartando…' : 'Confirmar descarte'}
            </PillButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
